import React, { useMemo } from 'react';
import { Flex, Button, Text, VStack, HStack } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { StepperStep } from '../AleaStepper/AleaStepper.tsx';
import AleaArrowRightIcon from '../../icons/AleaArrowRightIcon';
import AleaArrowLeftIcon from '../../icons/AleaArrowLeftIcon';

const getStepNavigationLabel = (
  step: StepperStep,
  displayStepNumber: number,
  t: TFunction
) => {
  const semanticLabel = t(`toStep${step.id}`);
  const colonIndex = semanticLabel.indexOf(':');
  if (colonIndex === -1) {
    return semanticLabel.replace(/\d+/, String(displayStepNumber));
  }

  const prefix = semanticLabel.slice(0, colonIndex).trim();
  const updatedPrefix = prefix.replace(/\d+/, String(displayStepNumber));
  return `${updatedPrefix}: ${step.title}`;
};

interface ResidentTabNavigationProps {
  handlePrev: () => void;
  handleNext: () => void;
  tabIndex: number;          // 0-based form tab index
  stepperTabIndices: number[]; // maps stepper index -> form tab index
  finalTabIndex: number;     // e.g. 4 if last visible step index is 3
  steps: StepperStep[];      // step titles/ids and disabled flags
  isEditMode?: boolean;     // true if editing existing resident, false if creating new
  isNextDisabled?: boolean;
}

const ResidentTabNavigation: React.FC<ResidentTabNavigationProps> = ({
  handlePrev,
  handleNext,
  tabIndex,
  stepperTabIndices,
  finalTabIndex,
  steps,
  isEditMode = false,
  isNextDisabled = false,
}) => {
  const { t } = useTranslation();

  const currentStepperIndex = Math.max(0, stepperTabIndices.indexOf(tabIndex));

  const prevEnabledIndex = useMemo(() => {
    for (let i = currentStepperIndex - 1; i >= 0; i--) {
      if (!steps[i]?.isDisabled) return i;
    }
    return -1;
  }, [currentStepperIndex, steps]);

  const nextEnabledIndex = useMemo(() => {
    for (let i = currentStepperIndex + 1; i < steps.length; i++) {
      if (!steps[i]?.isDisabled) return i;
    }
    return -1;
  }, [currentStepperIndex, steps]);

  const prevStep = prevEnabledIndex >= 0 ? steps[prevEnabledIndex] : undefined;
  const nextStep = nextEnabledIndex >= 0 ? steps[nextEnabledIndex] : undefined;

  const isFirstStep = currentStepperIndex === 0;
  const isFinal = tabIndex === (finalTabIndex - 1);

  return (
    <Flex
      ml="-25px"
      mr="-25px"
      h="175px"
      alignItems="flex-start"
      justifyContent="space-between"
      px="50px"
      pt="40px"
    >
      <VStack align="center" w="180px" spacing={2} style={{ visibility: isFirstStep ? 'hidden' : 'visible' }}>
        <Button
          colorScheme="white"
          variant="white"
          size="lg"
          onClick={handlePrev}
          border="1px solid red"
          borderRadius="20px"
          borderColor="alea.500"
        >
          <HStack>
            <AleaArrowLeftIcon size={21} />
            <Text fontWeight={400}>{t('backButton')}</Text>
          </HStack>
        </Button>
        {prevStep && (
          <Text fontSize="sm" color="gray.600" textAlign="center">
            {getStepNavigationLabel(prevStep, prevEnabledIndex + 1, t)}
          </Text>
        )}
      </VStack>

      <VStack align="center" w="180px">
        <Button
          colorScheme="alea"
          variant="solid"
          size="lg"
          onClick={handleNext}
          h="50px"
          borderRadius="20px"
          px="15px"
          py="10px"
          isDisabled={isNextDisabled}
          _disabled={{
            bg: '#E2E8F0',
            opacity: 1,
            cursor: 'not-allowed',
          }}
        >
          <HStack>
            <Text fontWeight={400}>
              {isFinal
                ? (isEditMode ? t('updateResident') : t('createResident'))
                : t('continueButton')
              }
            </Text>
            {!isFinal && (
              <AleaArrowRightIcon size={21} color='white' />
            )}
          </HStack>
        </Button>
        {nextStep && !isFinal && (
          <Text fontSize="sm" color="gray.600" textAlign="center">
            {getStepNavigationLabel(nextStep, nextEnabledIndex + 1, t)}
          </Text>
        )}
      </VStack>
    </Flex>
  );
};

export default ResidentTabNavigation;